import React, { useState, useContext, useEffect } from 'react'
import styled from 'styled-components'
import Portal from 'components/Portal'
import { bool } from 'prop-types'

import * as spacing from 'styles/spacing'
import { media } from 'styles/media'
import { white, borderColor, greyDark } from 'styles/colors'
import { navHeightMobile } from 'styles/global'

import { NativeAuthConsumer } from 'components/NativeAuth'
import Icon from 'components/Icon'
import MobileNavItem from './MobileNavItem'
import Cart from 'components/Nav/Cart'
import { MobileLogo, LogoWrap } from 'components/Nav'
import ProvinceSelector from 'components/Nav/ProvinceSelector'
import NativeAuthContext from 'components/NativeAuth'

import Hamburger from 'icons/hamburger.svg'
import Close from 'icons/close.svg'

const Toggle = styled.button`
  display: flex;
  padding: ${spacing.small};
  margin: -${spacing.small};

  ${media.medium`
    display: none;
  `};
`

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: ${white};
  padding-bottom: ${spacing.large};
  z-index: 800;
  display: ${props => (props.visibility ? 'block' : 'none')};

  ${media.medium`
    display: none;
  `};
`

const Inner = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
`

const Header = styled.header`
  position: relative;
  z-index: 10;
  height: ${navHeightMobile};
  box-shadow: 0 1px 0 0 ${borderColor};
  padding: 0 ${spacing.large};
  display: flex;
  align-items: center;
  justify-content: space-between;
`

const Scroll = styled.div`
  flex: 1;
  overflow-y: scroll;
  overflow-x: hidden;
  -webkit-overflow-scrolling: touch;
`

const List = styled.div`
  position: static;
  padding: ${spacing.small} 0;

  & + & {
    border-top: 1px solid ${borderColor};
  }
`

const CloseIcon = styled.div`
  display: flex;
  color: ${greyDark};
`

const MobileNav = ({ hideLogin }) => {
  const [visibility, setVisibility] = useState(false)
  const { province } = useContext(NativeAuthContext)

  useEffect(() => {
    document.body.style.overflow = visibility ? 'hidden' : 'visible'
    document.body.style.position = visibility ? 'fixed' : 'static'
  }, [visibility])

  const hideNav = () => {
    setVisibility(false)
  }

  return (
    <>
      <Toggle
        onClick={() => {
          setVisibility(!visibility)
        }}
      >
        <Icon icon={<Hamburger />} />
      </Toggle>
      <Portal selector={'vype-mobile-nav'}>
        <Overlay visibility={visibility}>
          <Inner>
            <Header>
              <Toggle
                onClick={() => {
                  setVisibility(!visibility)
                }}
              >
                <CloseIcon>
                  <Icon icon={<Close />} />
                </CloseIcon>
              </Toggle>
              <LogoWrap>
                <MobileLogo />
              </LogoWrap>
              <Cart />
            </Header>
            <Scroll>
              <List>
                <MobileNavItem hideNav={hideNav} label="Home" link="/" />
                {province !== 'BC' && (
                  <MobileNavItem
                    hideNav={hideNav}
                    label="ePen"
                    items={[
                      {
                        label: 'Device',
                        link: '/buy-online/epen-3/',
                        dataTracking: 'nav--link--text--ePen3_Starter_Kit'
                      },
                      {
                        label: 'Pods',
                        link: '/buy-online/vype-epen-3-cartridges/',
                        dataTracking: 'nav--link--text--ePen3_Cartridges'
                      }
                    ]}
                  />
                )}

                <MobileNavItem
                  hideNav={hideNav}
                  label="ePod"
                  items={[
                    {
                      label: 'Device',
                      link: '/vype-epod-starter-kit/',
                      dataTracking: 'nav--link--text--ePod_Starter_Kit'
                    },
                    {
                      label: 'Pods',
                      link: '/buy-online/vype-epod-vpro-cartridges/',
                      dataTracking: 'nav--link--text--ePod_Pods'
                    },
                    {
                      label: 'Skins',
                      link: '/buy-online/vype-epod-skins/',
                      dataTracking: 'nav--link--text--ePod_Skins'
                    },
                    {
                      label: 'Accessories',
                      link: '/buy-online/vype-epod-accessories/',
                      dataTracking: 'nav--link--text--ePod_Charger'
                    }
                  ]}
                />
              </List>

              <List>
                <MobileNavItem hideNav={hideNav} label="Subscribe & save" link="/subscribe/" />
                <MobileNavItem hideNav={hideNav} label="Vuse+" link="/vuse-plus-vape-deals/" />
                <MobileNavItem hideNav={hideNav} label="Benefits" link="/benefits" />
                {/* <MobileNavItem hideNav={hideNav} label="Savings" link="/referral" /> */}
                <MobileNavItem hideNav={hideNav} label="Contact us" href="https://batsupport.zendesk.com/hc/en-us" />
              </List>

              {/* Hide login and province selector */}
              {!hideLogin && (
                <List>
                  <NativeAuthConsumer>
                    {({ user, logout, setLoginModalVisibility }) => {
                      if (user) {
                        return (
                          <>
                            <MobileNavItem hideNav={hideNav} link="/account/rewards/" label="My account" />
                            <MobileNavItem
                              hideNav={hideNav}
                              as="button"
                              onClick={() => {
                                logout()
                                setVisibility(false)
                              }}
                              label="Logout"
                            />
                          </>
                        )
                      }

                      return (
                        <>
                          <MobileNavItem
                            hideNav={hideNav}
                            as="button"
                            link="#"
                            onClick={() => {
                              setLoginModalVisibility(true)
                              setVisibility(false)
                            }}
                            data-tracking="nav--link--button--Login/Register"
                            label="Login/Register"
                          />
                          <ProvinceSelector />
                        </>
                      )
                    }}
                  </NativeAuthConsumer>
                </List>
              )}

              {/* If hideLogin, show province selector but only if no user present */}
              {hideLogin && (
                <List>
                  <NativeAuthConsumer>
                    {({ user }) => {
                      if (user) {
                        return null
                      }

                      return <ProvinceSelector />
                    }}
                  </NativeAuthConsumer>
                </List>
              )}
            </Scroll>
          </Inner>
        </Overlay>
      </Portal>
    </>
  )
}

MobileNav.propTypes = {
  hideLogin: bool
}

export default MobileNav
